// ImportXlsForm.tsx
import React, { useMemo, useState } from 'react';
import { Box, Button, Chip, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRowSelectionModel, type GridRenderCellParams, type GridRowIdGetter } from '@mui/x-data-grid';
import { api } from "../../../api/plateApi";
import { useTranslation } from 'react-i18next';
import { ImportRecord, Plate } from '../../../types/plate.ts';
import { GroupSelect } from '../groups/GroupSelectExt.tsx';
import { getErrorMessage } from '../../../tools/error.ts';
import i18n from '../../../i18n';
import LocalizedGrid from '../../localizedGrid/LocalizedGridCook3h.tsx';
import { useAuthStore } from '../../../store/useAuthStore3.ts';

interface ImportXlsFormProps {
    data: ImportRecord[];
    exitsdata: Plate[];
    onClose: (referesh: boolean) => void;
}

export const ImportXlsForm: React.FC<ImportXlsFormProps> = ({data, exitsdata, onClose}) => {
    const {t} = useTranslation();
    const { customerId } = useAuthStore();
    const [selectionModel, setSelectionModel] = React.useState<number[]>([]);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [groupId, setGroupId] = React.useState<number | null>(null);

// onlyOwn section
    // const [onlyOwn, setOnlyOwn] = useState(!subtenantId);

    // const handleOwnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     setOnlyOwn(event.target.checked);
    // };

    // const filteredData = useMemo(() => {
    //     return !onlyOwn ? data : data.filter(row => !row.subtenant_id);
    // }, [data, onlyOwn]);
//

    const columns: GridColDef[] = [
        { field: "id", headerName: "ID", type: "number", flex: 1, width: 50, },
        { field: "registration_number", headerName: t('ImportForm.registration_number'), type: "number", flex: 1, width: 50, },
        { field: "car_brand", headerName: t('ImportForm.car_brand'), type: "string", flex: 1, width: 50, },
        { field: "car_owner", headerName: t('ImportForm.car_owner'), type: "string", flex: 1, width: 50, },
        // { field: "is_enabled", headerName: t('is_enabled'), type: "boolean", flex: 1, width: 50, },
        // { field: "is_otp", headerName: t('is_otp'), type: "boolean", flex: 1, width: 50, },
    ];
    
    const newNumbersCount = useMemo(() => {
        // Собираем Set существующих номеров для быстрой проверки
        const existingNumbers = new Set(exitsdata.map(exit => exit.registration_number));
        // Фильтруем data, оставляя только те, которых нет в Set
        const newRecords = data.filter(row => !existingNumbers.has(row.registration_number));
        return newRecords.length;
    }, [data, exitsdata]);

    const handleStoreToDB = async () => {
        setSubmitError(null);
        try {
            const data1 = data
                .filter((row) => selectionModel.includes(row.id))
                .map((row) => ({
                    ...row,
                    customer_id: customerId,
                    group_id: groupId || -1,
                }));
            await api.postPlates(data1 as Plate[]);
            onClose(true);
        } catch (error) {
          console.error("Save failed:", error);
          const errorMessage = getErrorMessage(
            error, 
            t('ImportForm.Failed_to_save_plates')
          );
          setSubmitError(errorMessage);
        }
    }

    const handleSelectionChange = (newSelectionModel: GridRowSelectionModel) => {
        setSelectionModel(newSelectionModel as number[]);
    };

    // const CustomToolbar = ({ count }: { count: number }) => {
    //     return (
    //         <Box sx={{ p: 2, display: 'flex', alignItems: 'center', borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
    //             <Typography variant="subtitle2" color="text.secondary">
    //                 Новых номеров, доступных для обработки: <b>{count}</b>
    //             </Typography>
    //         </Box>
    //     );
    // };

    const isRowDisabled = (row: ImportRecord) => {
        return exitsdata.some(
            (exit) => exit.registration_number === row.registration_number
        );
    };

    return (
        <>
            <Typography variant="h4">{t('ImportForm.title')}</Typography>
            
            {submitError && (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                {submitError}
            </Typography>
            )}
            
            {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, mt: 2 }}>
                <Chip
                    label={`З імпортованих записів нових: ${newNumbersCount}`} 
                    color="primary" 
                    variant="outlined" 
                    size="small" 
                />
            </Box>             */}
            <Paper 
            variant="outlined" 
            sx={{ 
                p: '12px 16px', 
                mb: 1, // небольшой отступ до таблицы
                display: 'flex', 
                alignItems: 'center',
                bgcolor: 'background.paper',
                borderColor: 'divider'
            }}
            >
                <Typography variant="body2" color="text.secondary">
                    Новых номеров для импорта:{' '}
                    <strong style={{ color: '#1976d2', fontSize: '1rem' }}>
                    {newNumbersCount}
                    </strong>
                </Typography>
            </Paper>

            <LocalizedGrid
            // <DataGrid<ImportRecord>
                gridId='ImportXlsForm'
                lang={i18n.language as any}
                rows={data}
                columns={columns} 
                getRowId={(row) => row.id} 
                rowSelectionModel={selectionModel}
                onRowSelectionModelChange={handleSelectionChange}
                checkboxSelection
                isRowSelectable={(params) => !isRowDisabled(params.row)}
                sx={{ 
                    maxWidth: '1200px',
                    // Стилизация для зашеренных строк прямо в sx
                    '& .disabled-row': {
                        opacity: 0.5,
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        // pointerEvents: 'none', // Опционально: отключает клики, если нужно визуально подчеркнуть "неактивность"
                    },
                }}
                // Добавляем класс для нужных строк
                getRowClassName={(params) => isRowDisabled(params.row) ? 'disabled-row' : ''}
                // getRowClassName={(params) => {
                //     const isExisted = exitsdata.some(
                //         (exit) => exit.registration_number === params.row.registration_number
                //     );
                //     return isExisted ? 'disabled-row' : '';
                // }}
                hideToolbar={true}
            />

            <Stack direction="row" spacing={2} sx={{ mb: 2, justifyContent: 'flex-end'}}>
                <Box width={200}>
                    <GroupSelect
                        selectedGroupId={groupId}
                        onSelect={(gid) => setGroupId(gid)}
                    />
                </Box>
                <Button 
                    variant="outlined" 
                    onClick={() => onClose(false)}
                >
                    {t('ImportForm.cancel')}
                </Button>
                <Button 
                    variant="contained" 
                    onClick={() => handleStoreToDB()}
                    disabled={selectionModel.length === 0 || !groupId}
                >
                    {t('ImportForm.StoreToDB')}
                </Button>
            </Stack>
        </>
    );
}